import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { api } from '../../../shared/services/API'
import { usePlatformAuth } from '../context/PlatformAuthContext'
import { PERM } from '../platformRoles'
import type { Jurisdicao } from '../regrasJuridicasModel'
import JurisdicaoFormDialog from '../components/JurisdicaoFormDialog'

/**
 * Catálogo de jurisdições e das suas regras jurídicas de assembleia. As regras nunca vêm pré-preenchidas: cada versão
 * é escrita por quem tem a fonte legal, submetida e validada por outra pessoa, e só então é aplicada.
 */
export default function RegrasJuridicas() {
    const navigate = useNavigate()
    const { hasPerm } = usePlatformAuth()
    const podeEditar = hasPerm(PERM.REGRAS_JURIDICAS_MANAGE)

    const [rows, setRows] = useState<Jurisdicao[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState('')
    const [formOpen, setFormOpen] = useState(false)
    const [editing, setEditing] = useState<Jurisdicao | null>(null)

    const carregar = useCallback(() => {
        setIsLoading(true)
        setError('')
        api.get<Jurisdicao[]>('/api/admin/regras-juridicas/jurisdicoes')
            .then((r) => setRows(r.data))
            .catch(() => setError('Não foi possível carregar as jurisdições.'))
            .finally(() => setIsLoading(false))
    }, [])

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- carregar alterna a sua própria flag de loading
        carregar()
    }, [carregar])

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Regras jurídicas</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Jurisdições e as regras de convocação, quórum e maioria das assembleias
                    </Typography>
                </Box>
                {podeEditar && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditing(null); setFormOpen(true) }}>
                        Nova jurisdição
                    </Button>
                )}
            </Box>

            <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
                A plataforma não traz regras de nenhum país. Uma jurisdição sem versão <b>validada</b> em vigor deixa as
                assembleias dos condomínios que a usam com a regra em falta: os resultados ficam inconclusivos.
            </Alert>

            {error && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Código</TableCell>
                                <TableCell>Nome</TableCell>
                                <TableCell>País</TableCell>
                                <TableCell>Regime jurídico</TableCell>
                                <TableCell align="right">Versões</TableCell>
                                <TableCell>Regra validada em vigor</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell align="right" sx={{ width: 64 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6 }}><CircularProgress size={24} /></TableCell></TableRow>
                            ) : rows.length === 0 ? (
                                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>Ainda não há jurisdições.</TableCell></TableRow>
                            ) : (
                                rows.map((j) => (
                                    <TableRow key={j.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/regras-juridicas/${j.id}`)}>
                                        <TableCell sx={{ fontWeight: 600 }}>{j.codigo}</TableCell>
                                        <TableCell>{j.nome}</TableCell>
                                        <TableCell>{[j.pais, j.regiao].filter(Boolean).join(' · ') || '—'}</TableCell>
                                        <TableCell sx={{ color: 'text.secondary', maxWidth: 260 }}>{j.regimeJuridico || '—'}</TableCell>
                                        <TableCell align="right">{j.versoes}</TableCell>
                                        <TableCell>
                                            {j.versaoValidada !== null ? (
                                                <Chip size="small" label={`Versão ${j.versaoValidada}`} sx={{ fontWeight: 600, bgcolor: '#D1FAE5', color: '#065F46' }} />
                                            ) : (
                                                <Chip size="small" label="Nenhuma — regra em falta" sx={{ fontWeight: 600, bgcolor: '#FEF3C7', color: '#92400E' }} />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={j.activa ? 'Activa' : 'Inactiva'}
                                                sx={{ fontWeight: 600, bgcolor: j.activa ? '#F0FDF4' : '#F3F4F6', color: j.activa ? '#15803D' : '#6B7280' }}
                                            />
                                        </TableCell>
                                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                            {podeEditar && (
                                                <Tooltip title="Editar jurisdição">
                                                    <IconButton size="small" onClick={() => { setEditing(j); setFormOpen(true) }}>
                                                        <EditOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <JurisdicaoFormDialog open={formOpen} initial={editing} onClose={() => setFormOpen(false)} onSaved={carregar} />
        </Box>
    )
}
